// ============================================================================
// cosmeticCatalog — the unlockable identity inventory (pure data)
// ----------------------------------------------------------------------------
// Phase 2 catalog for the Character vault and Profile showcase. Avatars can be
// linked to a Focus Guardian (see FOCUS_GUARDIANS) so equipping one drives the
// Horizon focus soundscape in Phase 3. Everything here is firebase-free.
// ============================================================================

import type { CosmeticRarity } from '../types/schema'
import { FOCUS_GUARDIANS } from './gamificationRegistry'

export type CosmeticSlot = 'AVATAR' | 'THEME' | 'BACKGROUND' | 'AUDIO' | 'ORBIT'

export const RARITY_ORDER: Record<CosmeticRarity, number> = {
  COMMON: 0,
  RARE: 1,
  EPIC: 2,
  LEGENDARY: 3,
}

/**
 * Profile level at which a cosmetic of each rarity becomes claimable in the
 * vault. TUNABLE — gives Phase 2 a working unlock loop without a credit shop.
 */
export const RARITY_UNLOCK_LEVEL: Record<CosmeticRarity, number> = {
  COMMON: 1,
  RARE: 3,
  EPIC: 6,
  LEGENDARY: 10,
}

export interface CosmeticItem {
  id: string
  name: string
  rarity: CosmeticRarity
  slot: CosmeticSlot
  description: string
}

export interface AvatarItem extends CosmeticItem {
  slot: 'AVATAR'
  emoji: string
  /** Links to FOCUS_GUARDIANS for the Horizon focus soundscape/particles. */
  guardianId?: string
}

export const AVATARS: AvatarItem[] = [
  { id: 'avatar_seedling', name: 'Seedling', rarity: 'COMMON', slot: 'AVATAR', emoji: '🌱', description: 'Where every timeline begins.' },
  { id: 'avatar_compass', name: 'Wayfinder', rarity: 'COMMON', slot: 'AVATAR', emoji: '🧭', description: 'Always pointed at the next milestone.' },
  { id: 'avatar_owl', name: 'Night Owl', rarity: 'RARE', slot: 'AVATAR', emoji: '🦉', description: 'Quiet hours, deep focus.' },
  { id: 'avatar_phoenix', name: 'Phoenix', rarity: 'RARE', slot: 'AVATAR', emoji: '🔥', description: 'Reborn from every broken streak.' },
  {
    id: 'avatar_alchemist',
    name: FOCUS_GUARDIANS.ALCHEMIST.displayName,
    rarity: FOCUS_GUARDIANS.ALCHEMIST.rarity,
    slot: 'AVATAR',
    emoji: '⚗️',
    description: 'Charcoal smoke and lo-fi rain. A Focus Guardian.',
    guardianId: FOCUS_GUARDIANS.ALCHEMIST.id,
  },
  {
    id: 'avatar_zen_master',
    name: FOCUS_GUARDIANS.ZEN_MASTER.displayName,
    rarity: FOCUS_GUARDIANS.ZEN_MASTER.rarity,
    slot: 'AVATAR',
    emoji: '🧘',
    description: 'Alpha waves and singing bowls. A Focus Guardian.',
    guardianId: FOCUS_GUARDIANS.ZEN_MASTER.id,
  },
  // Streak-reward tier — granted when consecutiveLoginStreak crosses each
  // LOGIN_STREAK_MILESTONES entry. Wave 7 · unblocks the streak reward flow.
  { id: 'avatar_streak_ember_10', name: 'Ember Keeper', rarity: 'RARE', slot: 'AVATAR', emoji: '🔥', description: 'A 10-day login streak.' },
  { id: 'avatar_streak_forge_25', name: 'Forge Warden', rarity: 'EPIC', slot: 'AVATAR', emoji: '🛡️', description: 'A 25-day login streak.' },
  { id: 'avatar_streak_beacon_50', name: 'Beacon Bearer', rarity: 'EPIC', slot: 'AVATAR', emoji: '🌟', description: 'A 50-day login streak.' },
  { id: 'avatar_streak_eternal_100', name: 'Eternal Flame', rarity: 'LEGENDARY', slot: 'AVATAR', emoji: '🕯️', description: 'A 100-day login streak.' },
]

/**
 * Wave 7 · Streak reward tier — maps LOGIN_STREAK_MILESTONES to cosmetic ids
 * granted when a user first crosses each milestone. Read by the streak
 * reward flow so the tier is data-driven, not hard-coded at the reward site.
 */
export const LOGIN_STREAK_REWARD_COSMETICS: Readonly<Record<number, string>> = {
  10: 'avatar_streak_ember_10',
  25: 'avatar_streak_forge_25',
  50: 'avatar_streak_beacon_50',
  100: 'avatar_streak_eternal_100',
} as const

export const THEMES: CosmeticItem[] = [
  { id: 'theme_midnight', name: 'Midnight', rarity: 'COMMON', slot: 'THEME', description: 'The default deep-space canvas.' },
  { id: 'theme_aurora', name: 'Aurora', rarity: 'RARE', slot: 'THEME', description: 'Soft green-violet horizon glow.' },
  { id: 'theme_ember', name: 'Ember', rarity: 'EPIC', slot: 'THEME', description: 'Warm coal gradients for late nights.' },
  { id: 'theme_celestial', name: 'Celestial', rarity: 'LEGENDARY', slot: 'THEME', description: 'Shifting starfield reserved for the dedicated.' },
  { id: 'theme_pearl', name: 'Pearl', rarity: 'COMMON', slot: 'THEME', description: 'Cool porcelain white with an iris-violet accent.' },
  { id: 'theme_linen', name: 'Linen & Gold', rarity: 'COMMON', slot: 'THEME', description: 'Warm ivory paper with champagne-gold accents.' },
  { id: 'theme_glacier', name: 'Glacier', rarity: 'RARE', slot: 'THEME', description: 'Icy blue-white with a deep teal accent.' },
  { id: 'theme_blush', name: 'Blush', rarity: 'EPIC', slot: 'THEME', description: 'Rose-quartz light with a raspberry accent.' },
  { id: 'theme_sage', name: 'Sage', rarity: 'LEGENDARY', slot: 'THEME', description: 'Soft green-grey paper with a forest-moss accent.' },
]

/**
 * Phase 2 Orbit wheel cosmetic skins (Phase 7 cosmetic shop integration).
 * Visual definitions and CSS classes reside in src/components/Common/Orbit/orbitStyles.css.
 */
export const ORBIT_SKINS: CosmeticItem[] = [
  { id: 'orbit_cream', name: 'Cream', rarity: 'COMMON', slot: 'ORBIT', description: 'The soft default — warm ivory arcs on shadow.' },
  { id: 'orbit_ivory', name: 'Ivory', rarity: 'COMMON', slot: 'ORBIT', description: 'Cleanest surface — pure white wedges.' },
  { id: 'orbit_glass', name: 'Glass', rarity: 'RARE', slot: 'ORBIT', description: 'Translucent halo — glass over your work.' },
  { id: 'orbit_ink', name: 'Ink', rarity: 'EPIC', slot: 'ORBIT', description: 'Deep charcoal — inverted for the night.' },
  { id: 'orbit_outline', name: 'Outline', rarity: 'LEGENDARY', slot: 'ORBIT', description: 'Bare frames — only strokes remain.' },
]

export const BACKGROUNDS: CosmeticItem[] = [
  { id: 'bg_grid', name: 'Blueprint Grid', rarity: 'COMMON', slot: 'BACKGROUND', description: 'Clean engineering grid.' },
  { id: 'bg_nebula', name: 'Nebula', rarity: 'RARE', slot: 'BACKGROUND', description: 'Drifting cosmic clouds.' },
  { id: 'bg_circuit', name: 'Circuit', rarity: 'EPIC', slot: 'BACKGROUND', description: 'Pulsing neon traces.' },
]

export const AUDIO_TRACKS: CosmeticItem[] = [
  { id: 'audio_silence', name: 'Silence', rarity: 'COMMON', slot: 'AUDIO', description: 'No ambient layer.' },
  { id: FOCUS_GUARDIANS.ALCHEMIST.audioId, name: FOCUS_GUARDIANS.ALCHEMIST.audioLabel, rarity: 'EPIC', slot: 'AUDIO', description: 'Bundled with The Alchemist.' },
  { id: FOCUS_GUARDIANS.ZEN_MASTER.audioId, name: FOCUS_GUARDIANS.ZEN_MASTER.audioLabel, rarity: 'LEGENDARY', slot: 'AUDIO', description: 'Bundled with The Zen Master.' },
]

export const TITLE_PREFIXES = ['Aspiring', 'Diligent', 'Relentless', 'Focused', 'Unstoppable', 'Legendary']
export const TITLE_SUFFIXES = ['of the Timeline', 'the Finisher', 'the Architect', 'the Closer', 'the Ascetic']

/** Cosmetics every account owns by default so a slot is never empty. */
export const DEFAULT_UNLOCKS = {
  avatars: ['avatar_seedling', 'avatar_compass'],
  themes: ['theme_midnight', 'theme_pearl'],
  backgrounds: ['bg_grid'],
  audio: ['audio_silence'],
  orbitSkins: ['orbit_cream'],
  titlePrefixes: ['Aspiring'],
  titleSuffixes: ['of the Timeline'],
} as const

export const DEFAULT_AVATAR_ID = DEFAULT_UNLOCKS.avatars[0]

const ALL_COSMETICS: CosmeticItem[] = [...AVATARS, ...THEMES, ...ORBIT_SKINS, ...BACKGROUNDS, ...AUDIO_TRACKS]

const COSMETIC_BY_ID = new Map(ALL_COSMETICS.map((item) => [item.id, item]))

export const getCosmeticById = (id: string): CosmeticItem | undefined => COSMETIC_BY_ID.get(id)

export const getAvatarById = (id: string): AvatarItem | undefined =>
  AVATARS.find((avatar) => avatar.id === id)

export const getCosmeticRarity = (id: string): CosmeticRarity =>
  COSMETIC_BY_ID.get(id)?.rarity ?? 'COMMON'

/** Catalog for a slot, sorted by ascending rarity then name. */
export const cosmeticsForSlot = (slot: CosmeticSlot): CosmeticItem[] =>
  ALL_COSMETICS
    .filter((item) => item.slot === slot)
    .sort((a, b) => RARITY_ORDER[a.rarity] - RARITY_ORDER[b.rarity] || a.name.localeCompare(b.name))
